import { all } from 'alkali'
import { Cache } from 'util/CacheIndex'
import { selectedStudyData } from 'model/StudyCollection'
import { selectedTreatments, cumulativeAllTreatments, evidenceSearch } from 'model/EvidenceSearch'
import {
	analyzeTreatmentAs,
	currentAnalysisSelections,
} from 'model/SearchQuery'

export const groupConnectivity = all(
	[selectedStudyData, selectedTreatments, analyzeTreatmentAs],
	async (selectedStudies, selectedTreatments, analyzeTreatmentAs) => {
		let analyzeAs = currentAnalysisSelections.get('analyzeAs')
		let disabledArms = new Set()
		for (let { label, disabled } of analyzeAs || []) {
			if (disabled) disabledArms.add(label)
		}
		const groupMap = new Cache()
		const groupSetsByKey = {}
		const comparisons = []
		for (let study of selectedStudies || []) {
			// react to any changes
			let analyzed = []
			for (let group of study.groups) {
				let name = group.analyzeAs
				if (name) {
					let groupSet = groupMap.get(name, () => ({
						name,
						groupMembers: [],
						groupCount: 0,
						connectedTo: [],
						description: name,
					}))
					groupSet.groupCount++
					if (disabledArms.has(name)) groupSet.disabled = true
					analyzed.push(name)
				}
			}
			for (let i = 0; i < analyzed.length; i++) {
				for (let j = i + 1; j < analyzed.length; j++) {
					let source = groupMap.get(analyzed[i])
					let target = groupMap.get(analyzed[j])
					if (!source.connectedTo.includes(target)) source.connectedTo.push(target)
					if (!target.connectedTo.includes(source)) target.connectedTo.push(source)
					comparisons.push({
						source,
						target,
						study,
						disabled: source.disabled || target.disabled,
					})
				}
			}
		}
		let allGroupingLabels = new Map()
		for (let { label } of analyzeAs || []) {
			allGroupingLabels.set(label, label)
		}
		for (let treatment of selectedTreatments) {
			allGroupingLabels.set(
				treatment.label,
				treatment.label +
					(treatment.contains
						? ': ' + treatment.contains.map((sub) => sub.label || sub.id || sub)?.join(', ')
						: '')
			)
		}
		for (let [interventionGroup, interventionDescription] of allGroupingLabels) {
			groupMap.get(interventionGroup, () => ({
				name: interventionGroup,
				groupCount: 1,
				connectedTo: [],
				groupSize: 0,
				id: '',
				groupMembers: [],
			})).description = interventionDescription
		}
		let groupSets = Array.from(groupMap.values())
		let connectedGroups = groupSets[0] ? [groupSets[0]] : []
		for (let i = 0; i < connectedGroups.length; i++) {
			let currentGroup = connectedGroups[i]
			for (let target of currentGroup.connectedTo) {
				if (!connectedGroups.includes(target)) connectedGroups.push(target)
			}
		}
		let disconnectedGroups = []
		for (let [interventionGroup] of allGroupingLabels) {
			if (!connectedGroups.some((connected) => connected.name == interventionGroup))
				disconnectedGroups.push(interventionGroup)
		}
		groupSets.forEach((element) => {
			selectedTreatments
				.valueOf()
				.filter((e) => e.label == element.name)
				.filter(function (e) {
					if (cumulativeAllTreatments.get(e.id) !== undefined && e.ids?.length > 1) {
						e.ids.filter(function (selectedTreatmentIds) {
							if (
								cumulativeAllTreatments.get(selectedTreatmentIds) !== undefined &&
								!element.groupMembers.includes(
									cumulativeAllTreatments.get(selectedTreatmentIds).label
								) &&
								!cumulativeAllTreatments.get(selectedTreatmentIds).label.includes(element.name)
							)
								element.groupMembers.push(
									cumulativeAllTreatments.get(selectedTreatmentIds).label
								)
						})
					} else if (cumulativeAllTreatments.get(e.id) !== undefined && e.id) {
						if (
							!element.groupMembers.includes(cumulativeAllTreatments.get(e.id).label) &&
							!cumulativeAllTreatments.get(e.id).label.includes(element.name)
						) {
							element.groupMembers.push(cumulativeAllTreatments.get(e.id).label)
						}
					}
				})

			element.groupSize = element.groupMembers.length
		})
		return {
			groupSets,
			comparisons,
			disconnectedGroups,
			complete: connectedGroups.length,
			incomplete: disconnectedGroups.length > 0,
		}
	}
)
export async function studyCountForComparisonEffect(interventionA, interventionB) {
	let comparisons = (await groupConnectivity).comparisons
	let studies = new Set()
	let searched = new Cache()
	function hasStudiesOnPath(a, b) {
		return searched.get(a + '->' + b, () => {
			searched.set(a + '->' + b, false)
			let hasConnections
			for (let { source, target, study } of comparisons.length ? comparisons : []) {
				if (source.name == a) {
					if (target.name == b) {
						studies.add(study)
						hasConnections = true
					} else if (hasStudiesOnPath(target.name, b)) {
						studies.add(study)
						hasConnections = true
					}
				} else if (target.name == a) {
					if (source.name == b) {
						studies.add(study)
						hasConnections = true
					} else if (hasStudiesOnPath(source.name, b)) {
						studies.add(study)
						hasConnections = true
					}
				}
			}
			return hasConnections
		})
	}
	hasStudiesOnPath(interventionA, interventionB)
	return studies.size
}
